import { useEffect, useState, useCallback, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  FaArrowLeft, FaInstagram, FaTrophy, FaLock,
  FaCheckCircle, FaVoteYea, FaStar, FaChevronDown, FaChevronUp, FaClock,
} from "react-icons/fa";
import "./StudioShowdownVotingPage.css";
import LoadingPage from "../../components/Loading/LoadingPage";
import { useAuth } from "../../contexts/AuthContext";

// ── Utilities ──────────────────────────────────────────────────────────────────

function getOrCreateDeviceId() {
  const key = "bc_device_id";
  let id = localStorage.getItem(key);
  if (!id) {
    id = "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
      const r = (Math.random() * 16) | 0;
      return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
    });
    localStorage.setItem(key, id);
  }
  return id;
}

function getVotedArtistsForRound(eventId, roundId) {
  try {
    const data = JSON.parse(localStorage.getItem(`bc_voted_rounds_${eventId}`) || "{}");
    return Array.isArray(data[roundId]) ? data[roundId] : [];
  } catch {
    return [];
  }
}

function saveVotedArtists(eventId, roundId, artistIds) {
  try {
    const key = `bc_voted_rounds_${eventId}`;
    const data = JSON.parse(localStorage.getItem(key) || "{}");
    data[roundId] = artistIds;
    localStorage.setItem(key, JSON.stringify(data));
  } catch {}
}

// ── Winner Announcement ────────────────────────────────────────────────────────

function WinnerAnnouncement({ results, round, votedIds, skipIntro, onReveal }) {
  // skipIntro = round was already closed when the page loaded (no live transition)
  const [phase, setPhase] = useState(skipIntro ? "reveal" : "drumroll");
  const [count, setCount] = useState(3);

  useEffect(() => {
    if (skipIntro) {
      // No countdown for already-complete rounds — reveal results after winner animates in
      const t = setTimeout(() => onReveal?.(), 700);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setPhase("countdown"), 600);
    return () => clearTimeout(t);
  }, [skipIntro]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== "countdown") return;
    if (count <= 0) {
      const t = setTimeout(() => { setPhase("reveal"); onReveal?.(); }, 200);
      return () => clearTimeout(t);
    }
    const t = setTimeout(() => setCount((c) => c - 1), 900);
    return () => clearTimeout(t);
  }, [phase, count]); // eslint-disable-line react-hooks/exhaustive-deps

  const advances = round.advances_count || 1;
  const winners = results.results.filter((a) => a.advanced === true).slice(0, advances);
  // Fallback: top N by sort order, matching advances_count exactly
  const displayWinners = winners.length > 0 ? winners : results.results.slice(0, advances);
  const userPickedWinner = displayWinners.some((w) => votedIds.includes(w.artist_id));

  if (phase === "drumroll") {
    return (
      <div className="winner-drumroll">
        <span className="winner-drumroll-text">And the winner is…</span>
        <div className="winner-drumroll-dots">
          <span /><span /><span />
        </div>
      </div>
    );
  }

  if (phase === "countdown") {
    return (
      <div className="winner-countdown">
        <span className="winner-countdown-label">Announcing in</span>
        <span key={count} className="winner-countdown-number">{count || "🎉"}</span>
      </div>
    );
  }

  return (
    <div className="winner-reveal">
      <div className="winner-confetti-ring" aria-hidden="true">
        {["🎉","⭐","🏆","✨","🎊","🌟","🎈","💫"].map((e, i) => (
          <span key={i} className="confetti-particle" style={{ "--i": i }}>{e}</span>
        ))}
      </div>
      <div className="winner-trophy-wrap">
        <FaTrophy className="winner-trophy-icon" />
      </div>
      <p className="winner-round-label">
        {round.round_name || `Round ${round.round_number}`} Winner{displayWinners.length > 1 ? "s" : ""}
      </p>
      <div className={`winner-names-grid${displayWinners.length === 1 ? " winner-names-grid--single" : ""}`}>
        {displayWinners.map((w) => (
          <div key={w.artist_id} className="winner-name-wrap">
            {w.image_url && <img src={w.image_url} alt={w.name} className="winner-avatar" />}
            <h2 className="winner-name">{w.name}</h2>
          </div>
        ))}
      </div>
      {userPickedWinner && (
        <div className="winner-you-picked">
          <FaCheckCircle /> You called it! Great pick!
        </div>
      )}
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────

function VoteResultBar({ artist, totalVotes, isAdvanced, isVotedFor }) {
  const pct = totalVotes > 0 ? Math.round((artist.vote_count / totalVotes) * 100) : 0;
  return (
    <div className={`vote-result-bar ${isVotedFor ? "vote-result-bar--voted" : ""} ${isAdvanced === false ? "vote-result-bar--eliminated" : ""}`}>
      <div className="vote-result-bar-header">
        <div className="vote-result-artist-info">
          {artist.image_url ? (
            <img src={artist.image_url} alt={artist.name} className="vote-result-avatar" />
          ) : (
            <div className="vote-result-avatar vote-result-avatar--placeholder"><FaStar /></div>
          )}
          <div className="vote-result-name-wrap">
            <span className="vote-result-name">{artist.name}</span>
            {isVotedFor && <span className="vote-result-your-vote">Your pick</span>}
          </div>
          {isAdvanced === true && <FaTrophy className="vote-result-trophy" />}
          {isAdvanced === false && <span className="vote-result-eliminated-badge">Eliminated</span>}
        </div>
        <span className="vote-result-pct">{pct}%</span>
      </div>
      <div className="vote-result-track">
        <div className="vote-result-fill" style={{ width: `${pct}%` }} />
      </div>
      <div className="vote-result-count">
        {artist.vote_count} {artist.vote_count === 1 ? "vote" : "votes"}
      </div>
    </div>
  );
}

function ArtistCard({ artist, selected, maxReached, onToggle }) {
  const disabled = maxReached && !selected;
  return (
    <button
      className={`artist-card ${selected ? "artist-card--selected" : ""} ${disabled ? "artist-card--disabled" : ""}`}
      onClick={() => !disabled && onToggle(artist.artist_id)}
      aria-pressed={selected}
    >
      <div className="artist-card-image-wrap">
        {artist.image_url ? (
          <img src={artist.image_url} alt={artist.name} className="artist-card-image" />
        ) : (
          <div className="artist-card-image artist-card-image--placeholder"><FaStar /></div>
        )}
        {selected && (
          <div className="artist-card-selected-overlay">
            <FaVoteYea className="artist-card-check" />
          </div>
        )}
      </div>
      <div className="artist-card-body">
        <h3 className="artist-card-name">{artist.name}</h3>
        {artist.bio && <p className="artist-card-bio">{artist.bio}</p>}
        {artist.instagram_url && (
          <a
            href={artist.instagram_url}
            target="_blank"
            rel="noopener noreferrer"
            className="artist-card-instagram"
            onClick={(e) => e.stopPropagation()}
          >
            <FaInstagram /><span>Instagram</span>
          </a>
        )}
      </div>
    </button>
  );
}

// ── Past Round (collapsed history) ────────────────────────────────────────────

function PastRoundCard({ round, eventId }) {
  const [open, setOpen] = useState(false);
  const [results, setResults] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";

  const votedIds = getVotedArtistsForRound(eventId, round.round_id);

  const load = useCallback(async () => {
    if (results) return;
    try {
      const res = await fetch(`${apiUrl}/showdown/events/${eventId}/rounds/${round.round_id}/results`);
      if (res.ok) setResults(await res.json());
    } catch {}
  }, [apiUrl, eventId, round.round_id, results]);

  const handleToggle = () => {
    setOpen((o) => !o);
    if (!open) load();
  };

  const roundLabel = round.round_name || `Round ${round.round_number}`;
  const advances = round.advances_count || 1;
  const advancedWinners = results ? results.results.filter((a) => a.advanced === true).slice(0, advances) : [];
  const displayPastWinners = advancedWinners.length > 0
    ? advancedWinners
    : (results?.results?.slice(0, advances) ?? []);

  return (
    <div className="past-round-card">
      <button className="past-round-toggle" onClick={handleToggle} aria-expanded={open}>
        <div className="past-round-toggle-left">
          <FaTrophy className="past-round-trophy" />
          <div>
            <span className="past-round-label">{roundLabel}</span>
            {displayPastWinners.length > 0 && !open && (
              <span className="past-round-winner-preview">
                {advances > 1 ? "Winners" : "Winner"}: {displayPastWinners.map((w) => w.name).join(", ")}
              </span>
            )}
          </div>
        </div>
        {open ? <FaChevronUp className="past-round-chevron" /> : <FaChevronDown className="past-round-chevron" />}
      </button>

      {open && (
        <div className="past-round-body">
          {!results ? (
            <p className="past-round-loading">Loading results…</p>
          ) : (
            <>
              {displayPastWinners.length > 0 && (
                <div className="past-round-winners-list">
                  {displayPastWinners.map((w) => (
                    <div key={w.artist_id} className="past-round-winner-row">
                      {w.image_url && <img src={w.image_url} alt={w.name} className="past-round-winner-avatar" />}
                      <div>
                        <span className="past-round-winner-name">{w.name}</span>
                        <span className="past-round-winner-badge">{advances > 1 ? "Advances" : "Winner"}</span>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              <div className="past-round-results">
                <p className="vote-results-total">{results.total_votes} total votes</p>
                {results.results.map((artist) => (
                  <VoteResultBar
                    key={artist.artist_id}
                    artist={artist}
                    totalVotes={results.total_votes}
                    isAdvanced={artist.advanced}
                    isVotedFor={votedIds.includes(artist.artist_id)}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

// ── Active Round Section ───────────────────────────────────────────────────────

function RoundSection({ round, eventId, customerToken, deviceId, onVoteSuccess, isLiveReveal }) {
  const maxVotes = Math.max(round.advances_count || 1, 1);

  const [selectedIds, setSelectedIds] = useState([]);
  const [votedIds, setVotedIds] = useState(() => getVotedArtistsForRound(eventId, round.round_id));
  const [results, setResults] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  // Always hide results until onReveal fires (after winner animation or countdown)
  const [resultsVisible, setResultsVisible] = useState(false);

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const hasVoted = votedIds.length > 0;
  const isOpen = round.voting_open && !round.is_complete;

  const fetchResults = useCallback(async () => {
    try {
      const res = await fetch(`${apiUrl}/showdown/events/${eventId}/rounds/${round.round_id}/results`);
      if (res.ok) setResults(await res.json());
    } catch {}
  }, [apiUrl, eventId, round.round_id]);

  // Fetch results whenever round closes or completes
  useEffect(() => {
    if (!isOpen) fetchResults();
  }, [isOpen, fetchResults]);

  // Cross-device vote check on mount
  useEffect(() => {
    if (hasVoted) return;
    (async () => {
      try {
        const params = new URLSearchParams();
        if (customerToken) params.set("customer_token", customerToken);
        else params.set("device_id", deviceId);
        const res = await fetch(`${apiUrl}/showdown/events/${eventId}/rounds/${round.round_id}/my-vote?${params}`);
        if (res.ok) {
          const data = await res.json();
          if (data.voted && data.artist_ids?.length > 0) {
            saveVotedArtists(eventId, round.round_id, data.artist_ids);
            setVotedIds(data.artist_ids);
          }
        }
      } catch {}
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleArtist = (artistId) => {
    setSelectedIds((prev) =>
      prev.includes(artistId)
        ? prev.filter((id) => id !== artistId)
        : prev.length < maxVotes ? [...prev, artistId] : prev
    );
  };

  const handleSubmit = async () => {
    if (selectedIds.length === 0) return;
    setSubmitting(true);
    setError("");
    try {
      const newVotedIds = [...votedIds];
      for (const artistId of selectedIds) {
        const res = await fetch(
          `${apiUrl}/showdown/events/${eventId}/rounds/${round.round_id}/vote`,
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              artist_id: artistId,
              customer_token: customerToken || null,
              device_id: !customerToken ? deviceId : null,
            }),
          }
        );
        if (res.status === 201 || res.status === 409) {
          if (!newVotedIds.includes(artistId)) newVotedIds.push(artistId);
        } else {
          const err = await res.json().catch(() => ({}));
          setError(err.detail || "Failed to submit vote. Please try again.");
          break;
        }
      }
      saveVotedArtists(eventId, round.round_id, newVotedIds);
      setVotedIds(newVotedIds);
      setSelectedIds([]);
      onVoteSuccess?.();
      requestAnimationFrame(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
        document.documentElement.scrollTo?.({ top: 0, behavior: "smooth" });
      });
    } catch {
      setError("Network error. Please check your connection and try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const roundLabel = round.round_name || `Round ${round.round_number}`;

  return (
    <section className="round-section">
      <div className="round-section-header">
        <div className="round-section-meta">
          <h2 className="round-section-title">{roundLabel}</h2>
          <span className="round-advances-badge">
            Pick {maxVotes} {maxVotes === 1 ? "artist" : "artists"}
          </span>
        </div>
        <div className={`round-status-pill ${isOpen ? "round-status-pill--open" : round.is_complete ? "round-status-pill--complete" : "round-status-pill--closed"}`}>
          {isOpen ? "Voting Open" : round.is_complete ? "Complete" : "Closed"}
        </div>
      </div>

      {/* Voting UI — open, not yet voted */}
      {isOpen && !hasVoted && (
        <>
          <p className="round-section-instruction">
            {maxVotes === 1
              ? "Select your favorite artist and cast your vote."
              : `Select up to ${maxVotes} artists you want to advance.`}
            {" "}{selectedIds.length > 0 && (
              <span className="selection-counter">{selectedIds.length} / {maxVotes} selected</span>
            )}
          </p>
          <div className="artist-grid">
            {round.artists.map((artist) => (
              <ArtistCard
                key={artist.artist_id}
                artist={artist}
                selected={selectedIds.includes(artist.artist_id)}
                maxReached={selectedIds.length >= maxVotes}
                onToggle={toggleArtist}
              />
            ))}
          </div>
          {error && <p className="vote-error">{error}</p>}
          <button
            className="vote-submit-btn"
            disabled={selectedIds.length === 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting
              ? "Submitting…"
              : selectedIds.length === 0
                ? `Select ${maxVotes === 1 ? "an artist" : "artists"} to vote`
                : `Submit ${selectedIds.length === 1 ? "Vote" : `${selectedIds.length} Votes`}`}
          </button>
        </>
      )}

      {/* Voted — waiting for round to close */}
      {isOpen && hasVoted && (
        <div className="voted-submitted">
          <div className="voted-submitted-icon-wrap">
            <FaVoteYea className="voted-submitted-icon" />
          </div>
          <h3 className="voted-submitted-title">Your vote is in!</h3>
          <p className="voted-submitted-desc">
            You voted for{" "}
            <strong>
              {votedIds
                .map((id) => round.artists.find((a) => a.artist_id === id)?.name)
                .filter(Boolean)
                .join(" & ")}
            </strong>.
          </p>
          <p className="voted-submitted-teaser">
            Results will be announced once this round closes — stay tuned!
          </p>
        </div>
      )}

      {/* Closed, not yet marked complete */}
      {!isOpen && !round.is_complete && (
        <div className="round-closed-notice">
          <FaLock className="round-closed-icon" />
          <p>Voting has closed. Results coming soon!</p>
        </div>
      )}

      {/* Closed, not complete — show standings */}
      {!isOpen && !round.is_complete && results && (
        <div className="vote-results">
          <h3 className="vote-results-title">Current standings</h3>
          <p className="vote-results-total">{results.total_votes} total {results.total_votes === 1 ? "vote" : "votes"}</p>
          {results.results.map((artist) => (
            <VoteResultBar
              key={artist.artist_id}
              artist={artist}
              totalVotes={results.total_votes}
              isAdvanced={artist.advanced}
              isVotedFor={votedIds.includes(artist.artist_id)}
            />
          ))}
        </div>
      )}

      {/* Complete — winner reveal + final results */}
      {round.is_complete && results && (
        <>
          <WinnerAnnouncement
            results={results}
            round={round}
            votedIds={votedIds}
            skipIntro={!isLiveReveal}
            onReveal={() => setResultsVisible(true)}
          />
          {resultsVisible && (
            <div className="vote-results">
              <h3 className="vote-results-title">Final Results</h3>
              <p className="vote-results-total">{results.total_votes} total {results.total_votes === 1 ? "vote" : "votes"}</p>
              {results.results.map((artist) => (
                <VoteResultBar
                  key={artist.artist_id}
                  artist={artist}
                  totalVotes={results.total_votes}
                  isAdvanced={artist.advanced}
                  isVotedFor={votedIds.includes(artist.artist_id)}
                />
              ))}
            </div>
          )}
        </>
      )}
    </section>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

export default function StudioShowdownVotingPage() {
  const { eventSlug } = useParams();
  const navigate = useNavigate();
  const { customerToken } = useAuth();

  const apiUrl = process.env.REACT_APP_API_URL || "http://localhost:8000";
  const deviceId = getOrCreateDeviceId();

  const [event, setEvent] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [voteCount, setVoteCount] = useState(0);
  const [now, setNow] = useState(() => new Date());

  // Keep `now` current so the event time gate reacts automatically
  useEffect(() => {
    const timer = setInterval(() => setNow(new Date()), 30_000);
    return () => clearInterval(timer);
  }, []);

  // Track which round just went live (is_complete flipped to true this session)
  const prevCompleteIdsRef = useRef(undefined); // undefined = not yet initialized
  const [liveRevealRoundId, setLiveRevealRoundId] = useState(null);

  // ── Fetch event ──
  useEffect(() => {
    let ok = true;
    (async () => {
      try {
        setLoading(true);
        const res = await fetch(`${apiUrl}/loyalty/events/${eventSlug}`);
        if (!res.ok) {
          setError(res.status === 404 ? "Event not found." : "Failed to load event.");
          setLoading(false);
          return;
        }
        const data = await res.json();
        if (!ok) return;
        setEvent(data);
        setLoading(false);
      } catch {
        if (!ok) return;
        setError("Failed to load event.");
        setLoading(false);
      }
    })();
    return () => { ok = false; };
  }, [eventSlug, apiUrl]);

  // ── Fetch rounds (on mount, on vote, and via polling) ──
  const fetchRounds = useCallback(async () => {
    if (!event?.event_id) return;
    try {
      const res = await fetch(`${apiUrl}/showdown/events/${event.event_id}/rounds`);
      if (res.ok) setRounds(await res.json());
    } catch {}
  }, [event?.event_id, apiUrl]);

  useEffect(() => {
    fetchRounds();
  }, [fetchRounds, voteCount]);

  // ── Detect is_complete flipping true (admin clicked "Reveal Results") ──
  useEffect(() => {
    const currentCompleteIds = new Set(rounds.filter((r) => r.is_complete).map((r) => r.round_id));
    const prev = prevCompleteIdsRef.current;
    if (prev !== undefined) {
      // Find any round that just became complete this poll cycle
      for (const id of currentCompleteIds) {
        if (!prev.has(id)) {
          setLiveRevealRoundId(id);
          break;
        }
      }
    }
    prevCompleteIdsRef.current = currentCompleteIds;
  }, [rounds]);

  // ── Poll every 4 s until every round is finalized (is_complete) ──
  useEffect(() => {
    const needsPoll = rounds.some((r) => !r.is_complete);
    if (!needsPoll || !event?.event_id) return;
    const interval = setInterval(fetchRounds, 4000);
    return () => clearInterval(interval);
  }, [rounds, event?.event_id, fetchRounds]);

  if (loading) return <LoadingPage />;

  if (error) {
    return (
      <div className="showdown-page">
        <div className="showdown-error">
          <h2>Something went wrong</h2>
          <p>{error}</p>
          <button className="showdown-back-btn" onClick={() => navigate(-1)}>
            <FaArrowLeft /> Go Back
          </button>
        </div>
      </div>
    );
  }

  // ── Event time gate ──
  const eventStart = event?.start_at ? new Date(event.start_at) : null;
  const eventEnd   = event?.end_at   ? new Date(event.end_at)   : null;
  const isBeforeEvent = eventStart && now < eventStart;
  const isAfterEvent  = eventEnd   && now > eventEnd;
  const isWithinEvent = !isBeforeEvent && !isAfterEvent;

  // Open round = actively accepting votes
  const openRound = rounds.find((r) => r.voting_open && !r.is_complete);
  // Revealed round = results made public by admin (is_complete=true), shown when no open round
  const revealedRound = openRound ? null : [...rounds].reverse().find((r) => r.is_complete);
  const displayRound = openRound || revealedRound;

  const formatEventTime = (dt) =>
    dt.toLocaleString("en-US", {
      weekday: "short", month: "short", day: "numeric",
      hour: "numeric", minute: "2-digit", hour12: true,
    });

  return (
    <div className="showdown-page">

      {/* Body */}
      <div className="showdown-body">
        {isBeforeEvent && (
          <div className="showdown-empty">
            <FaClock className="showdown-empty-icon" />
            <h2>Voting hasn't started yet</h2>
            <p>Voting opens {formatEventTime(eventStart)}. Come back then to cast your vote!</p>
          </div>
        )}

        {isAfterEvent && (
          <div className="showdown-empty">
            <FaLock className="showdown-empty-icon" />
            <h2>This event has ended</h2>
            <p>Voting closed {formatEventTime(eventEnd)}. Thanks for participating!</p>
          </div>
        )}

        {isWithinEvent && (
          <>
            {!displayRound ? (
              <div className="showdown-empty">
                <FaLock className="showdown-empty-icon" />
                <h2>Voting is currently closed</h2>
              </div>
            ) : (
              <RoundSection
                key={displayRound.round_id}
                round={displayRound}
                eventId={event.event_id}
                customerToken={customerToken}
                deviceId={deviceId}
                onVoteSuccess={() => setVoteCount((c) => c + 1)}
                isLiveReveal={liveRevealRoundId === displayRound.round_id}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
}
